import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { authenticatedBitswanBackendInstance } from '@/lib/api-client';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Plus, Trash2, Loader2, RefreshCw, AlertTriangle, CheckCircle2, Boxes } from 'lucide-react';
import { apiMessage } from '@/lib/pricing';

interface Region {
  id: string;
  name: string;
  slug: string;
  ingress_domain: string;
  namespace_prefix: string;
  server_memory: string;
  is_active: boolean;
  has_credentials: boolean;
  servers: number;
  last_checked_at: string | null;
  last_check_error: string;
  created_at: string;
  created_by: string;
}

interface ClusterFacts {
  version: string;
  git_version: string;
  nodes: number;
  may_create_namespaces: boolean;
  reason: string;
  memory_total_gb: number | null;
  memory_claimed_gb: number | null;
  namespaces: number | null;
}

const BLANK = {
  name: '',
  slug: '',
  kubeconfig: '',
  ingress_domain: '',
  namespace_prefix: 'bailey-',
  server_memory: '4Gi',
};

function room(facts: ClusterFacts | undefined): string | null {
  if (!facts || facts.memory_total_gb == null) return null;
  if (facts.memory_claimed_gb == null) return `${facts.memory_total_gb} GB in the cluster`;
  const left = Math.max(0, facts.memory_total_gb - facts.memory_claimed_gb);
  return `${facts.memory_claimed_gb} of ${facts.memory_total_gb} GB promised, ${left.toFixed(1)} GB free`;
}

function when(value: string | null): string {
  if (!value) return 'never';
  return new Date(value).toLocaleString();
}

export default function KubernetesRegionsPage() {
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ ...BLANK });
  const [editing, setEditing] = useState<Region | null>(null);
  const [replacement, setReplacement] = useState('');
  const [checked, setChecked] = useState<Record<string, ClusterFacts & { ok: boolean; error?: string }>>({});

  const { data, isLoading } = useQuery({
    queryKey: ['kubernetes-regions'],
    queryFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      return (await api.get('/kubernetes-clusters')).data.clusters as Region[];
    },
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['kubernetes-regions'] });

  const register = useMutation({
    mutationFn: async (body: typeof BLANK) => {
      const api = await authenticatedBitswanBackendInstance();
      return (await api.post('/kubernetes-clusters', body)).data;
    },
    onSuccess: (saved) => {
      toast.success(`${saved.name} is ready to take servers`);
      setAdding(false);
      setDraft({ ...BLANK });
      refresh();
    },
    onError: (error) => toast.error(apiMessage(error)),
  });

  const change = useMutation({
    mutationFn: async ({ id, body }: { id: string; body: Record<string, unknown> }) => {
      const api = await authenticatedBitswanBackendInstance();
      return (await api.patch(`/kubernetes-clusters/${id}`, body)).data;
    },
    onSuccess: () => {
      setEditing(null);
      setReplacement('');
      refresh();
    },
    onError: (error) => toast.error(apiMessage(error)),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const api = await authenticatedBitswanBackendInstance();
      return api.delete(`/kubernetes-clusters/${id}`);
    },
    onSuccess: () => {
      toast.success('Region removed');
      refresh();
    },
    onError: (error) => toast.error(apiMessage(error)),
  });

  const check = useMutation({
    mutationFn: async (id: string) => {
      const api = await authenticatedBitswanBackendInstance();
      return { id, ...(await api.post(`/kubernetes-clusters/${id}/check`)).data };
    },
    onSuccess: (result: any) => {
      setChecked((seen) => ({ ...seen, [result.id]: result }));
      if (result.ok) {
        toast.success(
          [`Kubernetes ${result.version}, ${result.nodes} node(s)`, room(result)]
            .filter(Boolean)
            .join(' · '),
        );
      }
      else toast.error(result.error ?? result.reason);
      refresh();
    },
    onError: (error) => toast.error(apiMessage(error)),
  });

  const regions = data ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Kubernetes regions</h1>
          <p className="text-muted-foreground mt-1 max-w-3xl text-sm">
            A region is a Kubernetes cluster this AOC may put servers in. Each server gets its own
            namespace, so one cluster holds as many as its memory allows.
          </p>
        </div>
        <Button onClick={() => setAdding(true)} data-testid="add-region">
          <Plus className="mr-2 h-4 w-4" />
          Add a region
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Boxes className="h-5 w-5" />
            Registered clusters
          </CardTitle>
          <CardDescription>
            Customers choose a region by name. They never see the credentials, and an inactive region
            keeps the servers already in it while taking no new ones.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : regions.length === 0 ? (
            <p className="text-muted-foreground py-10 text-center text-sm">
              No regions yet. Add one and customers can place a server in Kubernetes instead of
              renting a machine.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Region</TableHead>
                  <TableHead>Servers</TableHead>
                  <TableHead>Each gets</TableHead>
                  <TableHead>Room</TableHead>
                  <TableHead>Last checked</TableHead>
                  <TableHead>Taking new servers</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {regions.map((region) => {
                  const facts = checked[region.id];
                  return (
                    <TableRow key={region.id} data-testid={`region-${region.slug}`}>
                      <TableCell>
                        <div className="font-medium">{region.name}</div>
                        <div className="text-muted-foreground text-xs">
                          {region.namespace_prefix}&lt;server&gt;
                          {region.ingress_domain ? ` · ${region.ingress_domain}` : ''}
                        </div>
                      </TableCell>
                      <TableCell>{region.servers}</TableCell>
                      <TableCell className="font-mono text-sm">{region.server_memory}</TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {room(facts) ?? 'Check to see'}
                      </TableCell>
                      <TableCell className="text-sm">
                        <div>{when(region.last_checked_at)}</div>
                        {region.last_check_error ? (
                          <div className="text-destructive flex items-center gap-1 text-xs">
                            <AlertTriangle className="h-3 w-3" />
                            {region.last_check_error}
                          </div>
                        ) : facts?.ok ? (
                          <div className="flex items-center gap-1 text-xs text-green-600">
                            <CheckCircle2 className="h-3 w-3" />
                            Kubernetes {facts.version}, {facts.nodes} node(s)
                          </div>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={region.is_active}
                          onCheckedChange={(is_active) =>
                            change.mutate({ id: region.id, body: { is_active } })
                          }
                          data-testid={`region-active-${region.slug}`}
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => check.mutate(region.id)}
                            disabled={check.isPending}
                            data-testid={`region-check-${region.slug}`}
                          >
                            <RefreshCw className="mr-1 h-3 w-3" />
                            Check
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => setEditing(region)}>
                            Edit
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => remove.mutate(region.id)}
                            disabled={remove.isPending}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={adding} onOpenChange={setAdding}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add a region</DialogTitle>
            <DialogDescription>
              Paste a kubeconfig for an account that may create namespaces. It is checked against the
              cluster before anything is saved, so a region that appears here works.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4">
            <div className="grid gap-2">
              <Label htmlFor="region-name">Name</Label>
              <Input
                id="region-name"
                value={draft.name}
                placeholder="Frankfurt"
                onChange={(event) => setDraft({ ...draft, name: event.target.value })}
              />
              <p className="text-muted-foreground text-xs">What customers see when they choose where to put a server.</p>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="region-kubeconfig">Kubeconfig</Label>
              <Textarea
                id="region-kubeconfig"
                rows={10}
                className="font-mono text-xs"
                value={draft.kubeconfig}
                placeholder="apiVersion: v1&#10;kind: Config&#10;..."
                onChange={(event) => setDraft({ ...draft, kubeconfig: event.target.value })}
              />
              <p className="text-muted-foreground text-xs">
                Certificates and tokens must be embedded. A kubeconfig that points at files on your
                own machine will be refused.
              </p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="grid gap-2">
                <Label htmlFor="region-domain">Domain for servers here</Label>
                <Input
                  id="region-domain"
                  value={draft.ingress_domain}
                  placeholder="baileys.example.com"
                  onChange={(event) => setDraft({ ...draft, ingress_domain: event.target.value })}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="region-prefix">Namespace prefix</Label>
                <Input
                  id="region-prefix"
                  value={draft.namespace_prefix}
                  onChange={(event) => setDraft({ ...draft, namespace_prefix: event.target.value })}
                />
              </div>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="region-server-memory">Memory per server</Label>
              <Input
                id="region-server-memory"
                value={draft.server_memory}
                placeholder="4Gi"
                onChange={(event) => setDraft({ ...draft, server_memory: event.target.value })}
              />
              <p className="text-muted-foreground text-xs">
                A quota on each namespace. The cluster refuses a server that no longer fits
                rather than letting it push another one out.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdding(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => register.mutate(draft)}
              disabled={register.isPending || !draft.name || !draft.kubeconfig}
              data-testid="save-region"
            >
              {register.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Asking the cluster
                </>
              ) : (
                'Add region'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={editing !== null} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing?.name}</DialogTitle>
            <DialogDescription>
              {editing?.servers
                ? `${editing.servers} server(s) live here. Changing the domain or prefix does not move them.`
                : 'Nothing lives here yet.'}
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="grid gap-4">
              <div className="grid gap-2">
                <Label htmlFor="edit-name">Name</Label>
                <Input
                  id="edit-name"
                  value={editing.name}
                  onChange={(event) => setEditing({ ...editing, name: event.target.value })}
                />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="grid gap-2">
                  <Label htmlFor="edit-domain">Domain for servers here</Label>
                  <Input
                    id="edit-domain"
                    value={editing.ingress_domain}
                    onChange={(event) => setEditing({ ...editing, ingress_domain: event.target.value })}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="edit-server-memory">Memory per server</Label>
                  <Input
                    id="edit-server-memory"
                    value={editing.server_memory}
                    onChange={(event) => setEditing({ ...editing, server_memory: event.target.value })}
                  />
                  <p className="text-muted-foreground text-xs">
                    Servers already here keep their quota until they are provisioned again.
                  </p>
                </div>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit-kubeconfig">Replace the kubeconfig</Label>
                <Textarea
                  id="edit-kubeconfig"
                  rows={8}
                  className="font-mono text-xs"
                  value={replacement}
                  placeholder="Leave blank to keep the one already stored"
                  onChange={(event) => setReplacement(event.target.value)}
                />
                <p className="text-muted-foreground text-xs">
                  The stored one is never shown. A replacement is checked before it takes effect.
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button
              onClick={() =>
                editing &&
                change.mutate({
                  id: editing.id,
                  body: {
                    name: editing.name,
                    ingress_domain: editing.ingress_domain,
                    server_memory: editing.server_memory,
                    ...(replacement ? { kubeconfig: replacement } : {}),
                  },
                })
              }
              disabled={change.isPending}
            >
              {change.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
